import React from "react";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import { getSportEmoji } from "../../utils/sportIcons";
import {
  TEAM_MEMBER_ADMIN_ROLES,
  USER_ROLES,
  normalizeUserRole,
} from "../../constants/roles";
import TeamLogoUploader from "./TeamLogoUploader";

/**
 * Team Header Component
 * Displays team info, action buttons, and tab navigation
 */
const TeamHeader = ({
  team,
  currentUser,
  userMembership,
  activeTab,
  setActiveTab,
  onBack,
  onLeaveTeam,
  onEditTeam,
  onDeleteTeam,
  onInviteClick,
  onJoinClick,
  joinRequestStatus,
  canManageMembers,
  canChangeLogo = false,
  onLogoChange,
  returnTo,
  members,
  joinRequests,
  tournamentInvitations,
  successMessage,
  error,
  onClearError,
}) => {
  const renderJoinButton = () => {
    if (!currentUser) {
      return (
        <FloatingButton
          variant="primary"
          size="small"
          onClick={() => (window.location.href = "/login")}
          icon="👤"
        >
          <span className="btn-text">Login to Join</span>
        </FloatingButton>
      );
    }

    if (userMembership) {
      return (
        <FloatingButton variant="outline" size="small" onClick={onLeaveTeam} icon="🚪">
          <span className="btn-text">Leave Team</span>
        </FloatingButton>
      );
    }

    if (joinRequestStatus === "pending") {
      return (
        <div className="join-pending">
          <span className="icon">⏳</span>
          Your request is pending approval
        </div>
      );
    }

    if (joinRequestStatus === "rejected") {
      return (
        <div className="join-rejected">
          <span className="icon">❌</span>
          Request Rejected
        </div>
      );
    }

    if (joinRequestStatus === "inactive") {
      return (
        <FloatingButton variant="primary" size="small" onClick={onJoinClick} icon="🔄">
          <span className="btn-text">Rejoin Team</span>
        </FloatingButton>
      );
    }

    return (
      <FloatingButton
        variant="primary"
        size="small"
        onClick={onJoinClick}
        icon="➕"
      >
        <span className="btn-text">{team?.requires_approval ? "Request to Join" : "Join Team"}</span>
      </FloatingButton>
    );
  };

  const isTeamOwner = currentUser && Number(team?.created_by) === Number(currentUser.id);
  const normalizedRole = normalizeUserRole(currentUser?.role);
  const isSuperAdmin = normalizedRole === USER_ROLES.SUPER_ADMIN;
  const isActiveTeamMember = !!userMembership && userMembership.status === "active";
  const canInvite = isTeamOwner || isActiveTeamMember;
  const memberRole = userMembership?.role?.toLowerCase();
  const canManageTeam = isTeamOwner || isSuperAdmin || (currentUser && userMembership && TEAM_MEMBER_ADMIN_ROLES.includes(memberRole));

  return (
    <>
      <div className="teams-header-gaming">
        <FloatingCard className="header-card-gaming delay-1">
          <div className="teams-nav-gaming">
            <div className="teams-nav-left">
              <h2 className="teams-title-gaming">🏆 {team?.name || "Team Details"}</h2>
              <p className="teams-subtitle-gaming">
                Team information, members, and statistics
              </p>
            </div>
            <div className="teams-nav-right">
              <FloatingButton
                variant="outline"
                size="medium"
                onClick={onBack}
                icon="←"
              >
                <span className="btn-text">
                  Back
                </span>
              </FloatingButton>
            </div>
          </div>
        </FloatingCard>
      </div>

      <div className="team-details-content-gaming">
        <FloatingCard className="team-header-card-gaming delay-2">
          <div className="team-header-gaming">
            <div className="team-header-row">
              {/* Left side: Logo + Badges */}
              <div className="team-left-section">
                <div className="team-logo-section-gaming">
                  <TeamLogoUploader team={team} canEdit={canChangeLogo} onChange={onLogoChange} />
                </div>

                <div className="team-badges-inline">
                  <span className="sport-badge-gaming">
                    <span className="badge-icon">{getSportEmoji(team.sport_name || team.sport_type || team.sport)}</span>
                    {team.sport_name || team.sport_type || team.sport || "N/A"}
                  </span>
                  <span className={`privacy-badge-gaming ${team.is_public ? "public" : "private"}`}>
                    <span className="badge-icon">{team.is_public ? "🌐" : "🔒"}</span>
                    {team.is_public ? "Public" : "Private"}
                  </span>
                  {team.organization_name && (
                    <span className="org-badge-gaming">
                      <span className="badge-icon">🏢</span>
                      {team.organization_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Right side: Actions + Tabs */}
              <div className="team-right-section">
                <div className="team-actions-inline">
                  {renderJoinButton()}

                  {canInvite && (
                    <FloatingButton
                      variant="primary"
                      size="small"
                      onClick={onInviteClick}
                      icon="👥"
                    >
                      <span className="btn-text">Invite Players</span>
                    </FloatingButton>
                  )}

                  {canManageTeam && (
                    <>
                      <FloatingButton
                        variant="outline"
                        size="small"
                        onClick={onEditTeam}
                        icon="✏️"
                      >
                        <span className="btn-text">Edit</span>
                      </FloatingButton>

                      <FloatingButton
                        variant="danger"
                        size="small"
                        onClick={onDeleteTeam}
                        icon="🗑️"
                      >
                        <span className="btn-text">Delete</span>
                      </FloatingButton>
                    </>
                  )}

                  <FloatingButton
                    variant={activeTab === "overview" ? "primary" : "outline"}
                    size="small"
                    onClick={() => setActiveTab("overview")}
                    icon="📋"
                  >
                    <span className="btn-text">Overview</span>
                  </FloatingButton>

                  <FloatingButton
                    variant={activeTab === "members" ? "primary" : "outline"}
                    size="small"
                    onClick={() => setActiveTab("members")}
                    icon="👥"
                  >
                    <span className="btn-text">
                      Members ({Array.isArray(members) ? members.filter((m) => m.status === "active").length : 0}
                      {team && (team.max_members || team.settings?.max_members) ? `/${(team.max_members || team.settings?.max_members) + (team.max_substitutes ?? team.settings?.max_substitutes ?? team.settings?.rules?.substitutions ?? 0)}` : ""})
                    </span>
                  </FloatingButton>

                  {(isTeamOwner || canManageMembers) && (
                    <FloatingButton
                      variant={activeTab === "requests" ? "primary" : "outline"}
                      size="small"
                      onClick={() => setActiveTab("requests")}
                      icon="📝"
                    >
                      <span className="btn-text">Pending ({joinRequests.length + (Array.isArray(tournamentInvitations) ? tournamentInvitations.length : 0)})</span>
                    </FloatingButton>
                  )}

                  <FloatingButton
                    variant={activeTab === "stats" ? "primary" : "outline"}
                    size="small"
                    onClick={() => setActiveTab("stats")}
                    icon="📊"
                  >
                    <span className="btn-text">Statistics</span>
                  </FloatingButton>
                </div>
              </div>
            </div>

            {successMessage && (
              <div className="success-alert-gaming" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="alert-icon">✅</span>
                  <span className="alert-text">{successMessage}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="error-alert-gaming" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                  <span className="alert-icon">⚠️</span>
                  <span className="alert-text">{error}</span>
                </div>
                {onClearError && (
                  <button
                    onClick={onClearError}
                    aria-label="Dismiss"
                    style={{ background: 'none', border: '1px solid rgba(220,38,38,0.4)', borderRadius: '6px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: '0.2rem 0.5rem', fontSize: '0.8rem', flexShrink: 0, marginLeft: '0.5rem', lineHeight: 1 }}
                  >×</button>
                )}
              </div>
            )}
          </div>
        </FloatingCard>
      </div>
    </>
  );
};

export default TeamHeader;
